import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Mail, MessageCircle, Instagram, Clock, Plane, Compass, MapPinned } from "lucide-react";
import { Reveal, SplitLines } from "../components/Motion";
import { CONTACT_EMAIL, WHATSAPP_LINK, INSTAGRAM } from "../lib/config";
import Seo from "../components/Seo";

const COUNTRIES = [
  "Vietnam", "Laos", "Japan", "Singapore", "Kenya", "Malaysia", "UAE",
  "Hong Kong", "Cambodia", "Nepal", "Azerbaijan", "Kyrgyzstan", "Macau",
  "Sri Lanka", "Indonesia", "Thailand", "Maldives",
];

const FOUNDER_PHOTO = "https://customer-assets-rejwkqb3.emergentagent.net/job_explore-once/artifacts/exxvo12c_IMG_2860.JPG.webp";

// Small helper: floating decorative element that drifts as you scroll
function Drifter({ children, className = "", from = -60, to = 60 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [from, to]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-8, 8]);
  return (
    <motion.div ref={ref} style={{ y, rotate }} className={`pointer-events-none absolute ${className}`}>
      {children}
    </motion.div>
  );
}

export default function About() {
  return (
    <div className="pt-28 md:pt-36 pb-16 relative overflow-hidden" data-testid="about-page">
      <Seo
        title="About Us"
        description="Meet Aishwarya Karkera & Tejas G R — the travel couple behind Once Way Travel. 17 countries, small groups, and trips built to feel once-in-a-lifetime."
        path="/about"
      />
      {/* Background dashed flight-paths */}
      <svg className="absolute inset-x-0 top-0 w-full h-[900px] opacity-[0.09] pointer-events-none" viewBox="0 0 1600 900" preserveAspectRatio="none">
        <path d="M-20 200 Q 400 60 800 240 T 1620 180" stroke="currentColor" strokeWidth="1.2" fill="none" strokeDasharray="6 8" className="text-owt-forest"/>
        <path d="M-20 500 Q 500 700 1000 520 T 1620 620" stroke="currentColor" strokeWidth="1.2" fill="none" strokeDasharray="4 8" className="text-owt-terracotta"/>
        <circle cx="220" cy="180" r="4" className="fill-owt-forest"/>
        <circle cx="820" cy="240" r="4" className="fill-owt-forest"/>
        <circle cx="1100" cy="580" r="4" className="fill-owt-terracotta"/>
      </svg>

      {/* Drifting travel icons */}
      <Drifter className="top-40 right-10 md:right-24 text-owt-terracotta/50" from={-40} to={80}>
        <Plane className="w-10 h-10 md:w-14 md:h-14" strokeWidth={0.9} />
      </Drifter>
      <Drifter className="top-[46%] left-4 md:left-16 text-owt-forest/40" from={-80} to={40}>
        <Compass className="w-14 h-14 md:w-20 md:h-20 compass-spin" strokeWidth={0.6} />
      </Drifter>
      <Drifter className="top-[70%] right-8 md:right-32 text-owt-forest/40" from={40} to={-60}>
        <MapPinned className="w-10 h-10 md:w-12 md:h-12" strokeWidth={0.9} />
      </Drifter>

      <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
        {/* Header */}
        <Reveal>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">About Us</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] text-owt-forest max-w-4xl">
            <SplitLines text="Two travellers,"/>{" "}<br/>
            <span className="italic text-owt-terracotta"><SplitLines text="one way." delay={0.2}/></span>
          </h1>
        </Reveal>

        {/* Portrait + intro */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 mt-16 items-start">
          <motion.div
            className="md:col-span-5 relative"
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
          >
            <div className="relative">
              <img src={FOUNDER_PHOTO} alt="Aishwarya Karkera & Tejas G R" className="w-full aspect-[3/4] object-cover"/>
              <div className="absolute -bottom-4 -right-4 stamp bg-owt-surface">Est · 2026</div>
              {/* corner airmail border decoration */}
              <div className="absolute inset-0 border-4 border-transparent" style={{
                borderImage: "repeating-linear-gradient(45deg, transparent 0 6px, rgba(192,74,51,0.6) 6px 12px) 4",
                pointerEvents: "none",
              }}/>
            </div>
            <div className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted">Aishwarya Karkera · Tejas G R</div>
          </motion.div>

          <div className="md:col-span-7 space-y-6">
            <Reveal>
              <p className="font-display text-2xl md:text-3xl text-owt-forest leading-tight">
                We're <span className="text-owt-terracotta">Once Way Travel</span> — and we believe every trip should feel like a <em>one-way ticket to somewhere unforgettable.</em>
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-owt-ink/85 text-lg leading-relaxed">
                Once Way Travel was born out of a simple idea: the best trips aren't the ones you plan alone from a hundred browser tabs — they're the ones you take with <strong className="text-owt-forest">people who've already found the way.</strong>
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-owt-ink/85 text-lg leading-relaxed">
                We're <strong className="text-owt-forest">Aishwarya Karkera</strong> and <strong className="text-owt-forest">Tejas G R</strong>, and long before we were organizing trips for others, we were just two people obsessed with going places. What started as our own travels together has taken us across <span className="text-owt-terracotta font-semibold">17 countries</span> — Vietnam, Laos, Japan, Singapore, Kenya, Malaysia, UAE, Hong Kong, Cambodia, Nepal, Azerbaijan, Kyrgyzstan, Macau, Sri Lanka, Indonesia, Thailand, and the Maldives.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-owt-ink/85 text-lg leading-relaxed">
                Along the way, we learned the difference between a good trip and an unforgettable one — and <em>it's rarely about the five-star stay.</em> It's about the roads you take, the food you stumble upon, and <strong className="text-owt-forest">the people you experience it all with.</strong>
              </p>
            </Reveal>
          </div>
        </div>

        {/* Pull quote */}
        <Reveal>
          <div className="mt-24 md:mt-32 border-l-4 border-owt-terracotta pl-6 md:pl-12 py-6 max-w-4xl">
            <p className="font-display text-3xl md:text-5xl text-owt-forest leading-[1.05]">
              We kept getting the same message: <span className="italic text-owt-terracotta">"How do I do this too?"</span>
              <br/>
              So we decided to do both — keep sharing the journey, and start hosting it too.
            </p>
          </div>
        </Reveal>

        {/* Story continues */}
        <div className="grid md:grid-cols-12 gap-8 mt-16">
          <div className="md:col-span-5">
            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-3">Our promise</div>
              <h2 className="font-display text-4xl md:text-5xl text-owt-forest leading-[0.95]">
                That's <span className="italic text-owt-terracotta">Once Way Travel.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 space-y-6">
            <Reveal delay={0.1}>
              <p className="text-owt-ink/85 text-lg leading-relaxed">
                We plan the kind of trips <strong className="text-owt-forest">we'd want to be on ourselves</strong> — real, well-paced, a little adventurous, and never over-packaged. Every itinerary we put together is built to feel like a <em>once-in-a-lifetime trip</em>, not just another checkbox on a map. <strong className="text-owt-forest">You show up; we handle the rest.</strong>
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-display text-3xl md:text-4xl text-owt-terracotta leading-tight">
                One way in.<br/>A lifetime of memories out.
              </p>
            </Reveal>
          </div>
        </div>

        {/* 17 countries */}
        <section className="mt-24 md:mt-32 border-t border-owt-border pt-16 relative">
          <Reveal>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">Passport Stamps</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-owt-forest">Seventeen countries<br/><span className="italic text-owt-terracotta">— so far.</span></h2>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-10">
            {COUNTRIES.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.03, duration: 0.5 }}
                whileHover={{ y: -3, borderColor: "#C04A33" }}
                className="border border-dashed border-owt-border p-4 text-center transition-colors group"
              >
                <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-owt-muted group-hover:text-owt-terracotta">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-display text-lg text-owt-forest mt-1">{c}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mt-24 md:mt-32 border-t border-owt-border pt-16">
          <Reveal>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">Get in touch</div>
            <h2 className="font-display text-5xl md:text-6xl text-owt-forest">Where to find us.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            <a href={`mailto:${CONTACT_EMAIL}`} className="border border-owt-border p-6 hover:border-owt-terracotta group transition-colors" data-testid="about-email">
              <Mail className="w-5 h-5 text-owt-terracotta"/>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted mt-4">Email</div>
              <div className="font-display text-2xl text-owt-forest mt-1 group-hover:text-owt-terracotta">{CONTACT_EMAIL}</div>
            </a>
            <a href={WHATSAPP_LINK()} className="border border-owt-border p-6 hover:border-owt-terracotta group transition-colors" data-testid="about-whatsapp">
              <MessageCircle className="w-5 h-5 text-owt-terracotta"/>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted mt-4">WhatsApp</div>
              <div className="font-display text-2xl text-owt-forest mt-1 group-hover:text-owt-terracotta">+91 86603 38714</div>
            </a>
            <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="border border-owt-border p-6 hover:border-owt-terracotta group transition-colors" data-testid="about-instagram">
              <Instagram className="w-5 h-5 text-owt-terracotta"/>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted mt-4">Instagram</div>
              <div className="font-display text-2xl text-owt-forest mt-1 group-hover:text-owt-terracotta">@oncewaytravel</div>
            </a>
            <div className="border border-owt-border p-6">
              <MapPin className="w-5 h-5 text-owt-terracotta"/>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted mt-4">Studio</div>
              <div className="font-display text-2xl text-owt-forest mt-1">Bengaluru, India</div>
              <div className="flex items-center gap-2 mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted"><Clock className="w-3 h-3"/> Mon–Sat · 10:00–19:00 IST</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
